/* Offer ladder (blocks/_product-offer.liquid). Keeps the product form's variant id and quantity in
   step with the chosen card, and shows the price for two. The cart decides the real price; this only
   mirrors it (the kr saving for two set on each step, equal to the shop's automatic discount).
   Choice survives the section re-render on a colour change (sessionStorage per product).
   Two different colours go to the cart as two lines: the form carries them in data-offer-items,
   which assets/quick.js sends instead of the single id. */

const store = {
  get(key) {
    try {
      return JSON.parse(sessionStorage.getItem(key) || 'null');
    } catch {
      return null;
    }
  },
  set(key, value) {
    try {
      sessionStorage.setItem(key, JSON.stringify(value));
    } catch {}
  },
};

function money(cents, format) {
  const value = cents / 100;
  const whole = Number.isInteger(value);
  const fmt = (digits) =>
    value.toLocaleString('da-DK', { minimumFractionDigits: digits, maximumFractionDigits: digits });
  return (format || '{{amount_with_comma_separator}} kr').replace(/\{\{\s*(\w+)\s*\}\}/, (m, kind) => {
    if (kind.includes('no_decimals')) return fmt(0);
    return whole ? fmt(0) : fmt(2);
  });
}

class MwOffer extends HTMLElement {
  connectedCallback() {
    this.key = `mw-offer-${this.dataset.product}`;
    this.colour = this.dataset.colour;
    this.format = this.dataset.moneyFormat;
    this.cards = [...this.querySelectorAll('.offer__card[role="radio"]')];
    if (!this.cards.length) return;
    this.form = this.closest('.pdp__info')?.querySelector('form[data-product-form]');
    const saved = store.get(this.key) || {};
    const fallback = this.cards.find((c) => c.hasAttribute('data-recommended')) || this.cards[0];
    this.step = this.cards.some((c) => c.dataset.step === saved.step) ? saved.step : fallback.dataset.step;
    this.two = Boolean(saved.two);
    this.second = saved.second || this.colour;

    this.addEventListener('click', (event) => {
      const swatch = event.target.closest('[data-second-key]');
      if (swatch) {
        if (swatch.getAttribute('aria-disabled') !== 'true') this.update({ second: swatch.dataset.secondKey });
        return;
      }
      if (event.target.closest('[data-pair-toggle]')) {
        this.update({ two: !this.two });
        return;
      }
      const card = event.target.closest('.offer__card[role="radio"]');
      if (card) this.update({ step: card.dataset.step });
    });
    this.addEventListener('keydown', (event) => {
      // Second colour: one tab stop, arrows move between the colours this step has.
      const sw = event.target.closest('[data-second-key]');
      if (sw) {
        const all = [...this.querySelectorAll('[data-second-key]')].filter((x) => x.getAttribute('aria-disabled') !== 'true');
        const i = all.indexOf(sw);
        let to = null;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') to = all[(i + 1) % all.length];
        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') to = all[(i - 1 + all.length) % all.length];
        if (!to) return;
        event.preventDefault();
        this.update({ second: to.dataset.secondKey });
        this.querySelector(`[data-second-key="${to.dataset.secondKey}"]`)?.focus();
        return;
      }
      const card = event.target.closest('.offer__card[role="radio"]');
      if (!card) return;
      const i = this.cards.indexOf(card);
      let next = null;
      if (event.key === 'ArrowDown' || event.key === 'ArrowRight') next = this.cards[(i + 1) % this.cards.length];
      if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') next = this.cards[(i - 1 + this.cards.length) % this.cards.length];
      if (event.key === ' ' || event.key === 'Enter') next = card;
      if (!next) return;
      event.preventDefault();
      this.update({ step: next.dataset.step });
      next.focus();
    });
    this.update({});
  }

  card() {
    return this.cards.find((c) => c.dataset.step === this.step);
  }

  update(change) {
    Object.assign(this, change);
    store.set(this.key, { step: this.step, two: this.two, second: this.second });
    const chosen = this.card();

    this.cards.forEach((card) => {
      const on = card === chosen;
      card.setAttribute('aria-checked', String(on));
      card.tabIndex = on ? 0 : -1;
      const price = Number(card.dataset.price);
      const was = Number(card.dataset.was) || 0;
      const pairSave = Number(card.dataset.pairSave) || 0;
      const nowCents = this.two ? price * 2 - pairSave : price;
      const wasCents = this.two ? (was || price) * 2 : was;
      const wasEl = card.querySelector('[data-was]');
      const saveEl = card.querySelector('[data-save]');
      const showWas = wasCents > nowCents && (!this.two || on || was > 0);
      card.querySelector('[data-now]').textContent = money(nowCents, this.format);
      wasEl.hidden = !showWas;
      saveEl.hidden = !showWas;
      if (showWas) {
        wasEl.textContent = money(wasCents, this.format);
        saveEl.textContent = `Spar ${money(wasCents - nowCents, this.format)}`;
      }
    });

    // Buy for two: switch state and the second colour (colours the chosen step lacks are greyed out).
    const toggle = this.querySelector('[data-pair-toggle]');
    const secondBox = this.querySelector('[data-second]');
    const map = JSON.parse(chosen.dataset.map || '{}');
    if (toggle) toggle.setAttribute('aria-checked', String(this.two));
    if (secondBox) {
      secondBox.hidden = !this.two;
      if (!map[this.second]?.available) this.second = this.colour;
      secondBox.querySelectorAll('[data-second-key]').forEach((sw) => {
        const ok = Boolean(map[sw.dataset.secondKey]?.available);
        sw.setAttribute('aria-disabled', String(!ok));
        const stepName = chosen.querySelector('.offer__title')?.firstChild?.textContent.trim() || '';
        sw.setAttribute('aria-label', ok ? sw.dataset.secondName : `${sw.dataset.secondName}, ikke i ${stepName}`);
        sw.setAttribute('aria-checked', String(sw.dataset.secondKey === this.second));
        sw.tabIndex = sw.dataset.secondKey === this.second ? 0 : -1;
        if (sw.dataset.secondKey === this.second) {
          const name = secondBox.querySelector('[data-second-name]');
          if (name) name.textContent = sw.dataset.secondName;
        }
      });
    }

    // «Køb til to»: the line under it says what it saves, and free shipping when the total reaches it.
    const sub = this.querySelector('[data-pair-sub]');
    if (sub) {
      const pairSave = Number(chosen.dataset.pairSave) || 0;
      const totalTwo = Number(chosen.dataset.price) * 2 - pairSave;
      const free = Number(this.dataset.freeFrom) || 0;
      const single = Number(chosen.dataset.price);
      const parts = [];
      if (pairSave) parts.push(`Spar ${money(pairSave, this.format)}`);
      // Free shipping only when it is a real gain: one is under the limit, two are over it.
      if (free && single < free && totalTwo >= free) parts.push(parts.length ? 'fri fragt' : 'Fri fragt');
      // Once ticked, the card shows the saving; the row only asks for the colour.
      sub.textContent = !this.two && parts.length ? `${parts.join(' og ')}. Vælg farve til hver.` : 'Vælg farve til hver.';
    }
    this.closest('.pdp__info')?.classList.toggle('offer-two', this.two);

    // The picker's own (hidden) step follows the card, so a colour change re-renders on this step
    // and the server's price matches the card (snippets/offer-default-variant opens on the default).
    if (chosen.dataset.option) {
      const radio = [...(this.closest('.pdp__info')?.querySelectorAll('mw-variant-picker input[type="radio"]') || [])].find(
        (input) => input.value === chosen.dataset.option
      );
      if (radio && !radio.checked) radio.checked = true;
    }

    // The form: variant id, quantity, and two lines when the colours differ.
    if (!this.form) return;
    const first = map[this.colour] || map.one;
    if (!first) return;
    const idInput = this.form.querySelector('[name="id"]');
    const qtyInput = this.form.querySelector('[name="quantity"]');
    if (idInput) idInput.value = first.id;
    const differs = this.two && this.second !== this.colour && map[this.second];
    if (qtyInput) qtyInput.value = this.two && !differs ? 2 : 1;
    if (differs) {
      this.form.dataset.offerItems = JSON.stringify([
        { id: first.id, quantity: 1 },
        { id: map[this.second].id, quantity: 1 },
      ]);
    } else {
      delete this.form.dataset.offerItems;
    }

    // The button says what is bought and what it costs.
    const price = Number(chosen.dataset.price);
    const total = this.two ? price * 2 - (Number(chosen.dataset.pairSave) || 0) : price;
    const label = this.two ? this.dataset.pairLabel : this.dataset.addLabel;
    // The sticky bar (sections/main-product.liquid) says the same: step name and total.
    const stepName = chosen.querySelector('.offer__title')?.firstChild?.textContent.trim() || '';
    const sticky = this.closest('.pdp')?.querySelector('mw-sticky-cart');
    const stickyPrice = sticky?.querySelector('.pdp-sticky__price');
    if (stickyPrice) stickyPrice.textContent = `${this.two ? '2 × ' : ''}${stepName} · ${money(total, this.format)}`;
    // On a phone the bar is only the button, so the button carries the price (hidden on desktop,
    // where the line beside the image already says it).
    const stickyAdd = sticky?.querySelector('.pdp-sticky__add');
    if (stickyAdd) {
      // One inline span, as in the main button, so the button's flex gap does not split the line.
      const line = document.createElement('span');
      const p = document.createElement('span');
      p.className = 'pdp-sticky__add-price';
      p.textContent = ` · ${money(total, this.format)}`;
      line.append(label, p);
      stickyAdd.replaceChildren(line);
    }

    // The delivery panel's free-shipping line follows the choice (assets/delivery-panel.js).
    document.dispatchEvent(new CustomEvent('mw:offer:change', { detail: { total, two: this.two, step: this.step } }));

    // The big price above follows the chosen card.
    this.closest('.pdp__info')?.querySelectorAll('.pdp-head .price__current').forEach((el) => {
      const hidden = el.querySelector('.visually-hidden');
      el.textContent = ' ' + money(total, this.format);
      if (hidden) el.prepend(hidden);
    });
    this.form.querySelectorAll('.pform__add-idle').forEach((idle) => {
      idle.innerHTML = '';
      idle.append(document.createTextNode(label + ' '));
      const p = document.createElement('span');
      p.className = 'pform__add-price';
      p.textContent = money(total, this.format);
      idle.append(p);
    });
  }
}

if (!customElements.get('mw-offer')) customElements.define('mw-offer', MwOffer);
